/*
  bf_turntakeover.ts
  Copyright (c) 2013-present Human Cube Inc.

  Does zone takeovers. Must all be synchronous and not involve any external DB or file access.
  Also does not access any external user data.
  These restrictions are to allow it to be re-used in other environments such as the client.
*/

import BF from '../bfconst1';
import bfH from '../bf_helper';
import { Game } from '../gameTypes';
import bfTurnReplay from './bf_turnreplay';


export function doTakeover (game: Game) {
  // Resolve all ownership changes for all zones.

  const takeOver = calcTakeOverBits(game);

  for(let zoneID = 1; zoneID <= game.zones; zoneID++) {
    if( !bfH.isLand(game, zoneID) ) {
      continue;  // Only land tracks ownership.
    }

    const currentOwner = game.zoneOwner[zoneID];
    if(takeOver[zoneID].hold[currentOwner]) {
      continue;  // Still some defenders (including allies), so no ownership change.
    }

    // No defenders left to hold, so change ownership.
    // Remember that the hold value already contains allies values as well.
    let newOwner = -1;
    for(let i = 0; i <= game.players; i++) {
      if( takeOver[zoneID].claim[i] ) {
        if( newOwner === -1 ) {
          newOwner = i;
        }
        else {
          newOwner = -2;  // More than one player here.
          break;
        }
      }
    }

    if(newOwner === -2) {
      // There is more than one victorious attacker.
      // Check if they are on the same team, if so, give to larger one,
      // else just let the nation go neutral.

      let teamPlayerIndex = -1;
      for(let i = 0; i <= game.players; i++) {
        if( takeOver[zoneID].claim[i] ) {
          if( teamPlayerIndex === -1 ) {
            teamPlayerIndex = i;
          }
          else {
            if( !(game.fixedAlly[teamPlayerIndex] & (1 << i)) ) {
              // A non-ally is also here.
              teamPlayerIndex = -2;  // so more than one team here.
              break;
            }
          }
        }
      }

      if(teamPlayerIndex >= 0) {
        // Single team victor attacker, so choose largest teammate:
        let biggestIndex = -1;
        let biggestCount = 0;
        for(let i = 0; i <= game.players; i++) {
          if( takeOver[zoneID].claim[i] > biggestCount ) {
            // In the event of a tie the higher playerindex gets it (no big deal).
            biggestIndex = i;
            biggestCount = takeOver[zoneID].claim[i];
          }
          else if(takeOver[zoneID].claim[i] === biggestCount &&
            Math.random() >= 0.5 ) {
            // In the event of a tie, make it 50% chance to select the
            // other teammate as the takeover victor.
            biggestIndex = i;
            biggestCount = takeOver[zoneID].claim[i];
          }
        }
        if(biggestIndex >= 0 && biggestCount > 0) {
          newOwner = biggestIndex;  // Assign the country to the team member
                                    // with the most attacking land units.
        }
      }
    }

    if( newOwner >= 0 ) {
      // Give it to the single land controller or if it is their allies capital, back to them.
      if( currentOwner !== newOwner ) {
        // Check if liberating a capital for an ally, if so give it back to
        // them (only for capitals):
        for(let i = 0; i <= game.players; i++) {
          if( !game.finished[i][0] &&        // Still in the game.
              game.capital[i] === zoneID &&  // It is their capital.
              (game.fixedAlly[newOwner] & (1<<i)) ) {  // Is an ally.
            // This is the capital of an ally, so give it back to them:
            newOwner = i;
            break;
          }
        }

        bfTurnReplay.takeover(game, zoneID);
        game.zoneOwner[zoneID] = newOwner;
        changeOwnershipOfZoneChangeForces(game, zoneID, newOwner);
      }
    }
    else if( newOwner === -2 ) {
      // Nation is going neutral because there surface controllers of different
      // teams and no defenders:
      if( currentOwner !== 0 ) {
        bfTurnReplay.takeover(game, zoneID);
        game.zoneOwner[zoneID] = 0;
        changeOwnershipOfZoneChangeForces(game, zoneID, 0);
      }
    }
  }
}


function calcTakeOverBits (game: Game) {
  // Returns an array of player hold and claim counts for each zone (indexed on zoneID-1).
  const zones = game.zones;
  const takeOver: {claim: number[], hold: number[]}[] = [];
  for(let i = 0; i <= zones; i++) {
    takeOver[i] = {claim: [], hold: []};
    for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) { // all players and 0.
        takeOver[i].claim[playerIndex] = 0;
        takeOver[i].hold[playerIndex] = 0;
    }
  }
  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) { // all players and 0.
    const allyBits = game.fixedAlly[playerIndex];
    for(let j = 0, len = game.force[playerIndex].length; j < len; j++) {
      let zoneID = game.force[playerIndex][j][BF.FORCE_INDEX_ZONE];
      if( zoneID > 0 && zoneID <= zones ) {
        let forceFlags = game.forceFlags[game.force[playerIndex][j][BF.FORCE_INDEX_TYPE]];
        if( forceFlags & BF.FORCE_FLAG_CAN_CLAIM ) {
          takeOver[zoneID].claim[playerIndex]++;
        }
        if( forceFlags & BF.FORCE_FLAG_CAN_HOLD ) {
          // Hold for this player and any allies as well.
          for(let p = 0; p <= game.players; p++) {
            if( allyBits & (1 << p) ) {
              takeOver[zoneID].hold[p]++;
            }
          }
        }
      }
    }
  }
  return takeOver;
}


function changeOwnershipOfZoneChangeForces (game: Game, zoneID: number, newOwner: number) {
  // Change ownership of any forces that change owner based on zone owner (eg Factories).

  for(let playerIndex = 0; playerIndex <= game.players; playerIndex++) { // all players and 0.
    if( playerIndex !== newOwner ) {
      for(let j = game.force[playerIndex].length - 1; j >= 0; j--) {
        if( game.force[playerIndex][j][BF.FORCE_INDEX_ZONE] === zoneID ) {
          const forceFlags = game.forceFlags[game.force[playerIndex][j][BF.FORCE_INDEX_TYPE]];
          if( forceFlags & BF.FORCE_FLAG_OWNED_BY_ZONE_OWNER ) {
            // Change owner:
            const forceID = game.force[playerIndex][j][BF.FORCE_INDEX_ID];
            game.force[newOwner].push(game.force[playerIndex][j]);
            game.force[playerIndex].splice(j, 1);
            bfTurnReplay.capture(game, forceID, playerIndex, newOwner);
          }
        }
      }
    }
  }
}
